import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Avatar,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  CalendarToday as CalendarTodayIcon,
} from "@mui/icons-material";
import { activateDemoToTenant } from "../../../Services/BrokersApi";
import { getPlane } from "../../../Services/plansApi";
import { themeColors } from "../../../theme/colors";

const ActivateDemoDialog = ({ open, tenantId, onClose, onActivated }) => {
  const [planeId, setPlaneId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [planeOptions, setPlaneOptions] = useState([]);
  const [loadingPlanes, setLoadingPlanes] = useState(false);

  // عند فتح الحوار، اجلب الباقات من API
  useEffect(() => {
    if (!open) {
      // عند إغلاق الحوار أعد تهيئة القيم
      setPlaneId("");
      setStartDate("");
      setEndDate("");
      setError(null);
      return;
    }

    // جلب الباقات
    const fetchPlanes = async () => {
      setLoadingPlanes(true);
      setError(null);
      try {
        const token = localStorage.getItem("authToken");
        const res = await getPlane(null, token);
        // استخراج المصفوفة من الاستجابة (دعم أشكال مختلفة)
        const planesList =
          res?.data?.data?.palnes ||
          res?.data?.data?.planes ||
          res?.data?.palnes ||
          res?.data?.planes ||
          res?.palnes ||
          res?.planes ||
          res?.data ||
          res ||
          [];
        const options = (Array.isArray(planesList) ? planesList : []).map((p) => ({
          id: String(p.id),
          name: p.plane_name ?? p.name ?? p.slug ?? `Plane ${p.id}`,
        }));
        setPlaneOptions(options);
        // اختر أول باقة افتراضياً
        setPlaneId(options[0]?.id ?? "");
      } catch (err) {
        setError((err && (err.message || err.msg || JSON.stringify(err))) || "فشل جلب الباقات");
      } finally {
        setLoadingPlanes(false);
      }
    };

    fetchPlanes();
  }, [open]);

  const handleSubmit = async () => {
    if (!tenantId) {
      setError("tenant_id مطلوب");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      const payload = {
        tenant_id: tenantId,
        plane_id: planeId || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      await activateDemoToTenant(payload, token);
      if (onActivated) onActivated();
    } catch (err) {
      setError((err && (err.message || err.msg || JSON.stringify(err))) || "فشل تفعيل الديمو");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 2,
          background: `linear-gradient(135deg, ${themeColors.warning} 0%, #F57C00 100%)`,
          color: "white",
          position: "relative",
        }}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: "rgba(255,255,255,0.25)",
            backdropFilter: "blur(10px)",
          }}
        >
          <CheckCircleIcon />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            تفعيل النسخة التجريبية
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            تحويل النسخة من تجريبية إلى نسخة كاملة مفعلة
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            left: 8,
            top: 8,
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.15)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(220, 53, 69, 0.15)",
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {loadingPlanes && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={40} sx={{ color: themeColors.warning }} />
            <Typography variant="body2" sx={{ ml: 2, color: themeColors.text.secondary }}>
              جاري تحميل الباقات...
            </Typography>
          </Box>
        )}

        {!loadingPlanes && (
          <Box display="flex" flexDirection="column" gap={2.5}>
            <TextField
              label="معرّف النسخة"
              value={tenantId ?? ""}
              disabled
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: themeColors.background.hover,
                },
              }}
            />

            <TextField
              select
              label="اختر الباقة"
              value={planeId}
              onChange={(e) => setPlaneId(e.target.value)}
              fullWidth
              helperText={planeOptions.length === 0 ? "⚠️ لا توجد باقات متاحة" : `✓ ${planeOptions.length} باقة متاحة`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: themeColors.warning,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.warning,
                  },
                },
              }}
            >
              {planeOptions.length === 0 ? (
                <MenuItem value="" disabled>
                  لا توجد باقات
                </MenuItem>
              ) : (
                planeOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: themeColors.warning,
                        }}
                      />
                      {opt.name}
                    </Box>
                  </MenuItem>
                ))
              )}
            </TextField>

            <Box display="flex" gap={2}>
              <TextField
                label="تاريخ البداية"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.warning,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.warning,
                    },
                  },
                }}
              />

              <TextField
                label="تاريخ النهاية"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.warning,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.warning,
                    },
                  },
                }}
              />
            </Box>
          </Box>
        )}

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: `${themeColors.warning}10`,
            borderRadius: 2,
            border: `1px solid ${themeColors.warning}30`,
          }}
        >
          <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
            ⚡ <strong>تنبيه:</strong> سيتم تحويل النسخة التجريبية إلى نسخة كاملة مفعلة بالباقة والتواريخ المحددة
          </Typography>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            borderColor: themeColors.border.main,
            color: themeColors.text.secondary,
            "&:hover": {
              borderColor: themeColors.text.primary,
              bgcolor: "transparent",
            },
          }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || loadingPlanes || planeOptions.length === 0}
          sx={{
            borderRadius: 2,
            px: 4,
            bgcolor: themeColors.warning,
            boxShadow: `0 4px 12px ${themeColors.warning}40`,
            "&:hover": {
              bgcolor: "#F57C00",
              boxShadow: `0 6px 16px ${themeColors.warning}60`,
            },
            "&:disabled": {
              bgcolor: themeColors.text.light,
            },
          }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              <span>جاري التفعيل...</span>
            </Box>
          ) : (
            <>
              <CheckCircleIcon sx={{ mr: 1, fontSize: 20 }} />
              تفعيل النسخة
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivateDemoDialog;
